import type { ReactNode } from "react";

import { closeAllToasts, closeToast, showToast, type ToastButton, type ToastTone } from "@/lib/toastStore";

/**
 * The sonner-style toast API (`toast(title, options)`, `toast.success()`,
 * `toast.error()`, `toast.dismiss()`), drawn by the one staff toast system
 * (src/lib/toastStore.ts, src/components/ui/toaster.tsx). Same options and
 * callbacks as sonner 1.7 had here.
 */
export interface ToastOptions {
  /** A toast shown under an id that is still up is replaced in place. */
  id?: string | number;
  description?: ReactNode;
  /**
   * `Infinity`: stays until closed (a KOT that didn't print, a guest calling a
   * waiter). Any other value is ignored: every other toast closes after 5 seconds.
   */
  duration?: number;
  /** Every toast has its X; kept so existing calls compile. */
  dismissible?: boolean;
  action?: ToastButton;
  /** Closed with its X (or Escape). Not called for an action click or toast.dismiss(). */
  onDismiss?: (toast: { id: string }) => void;
  /** Its countdown ran out. */
  onAutoClose?: (toast: { id: string }) => void;
}

function show(tone: ToastTone, message: ReactNode, options: ToastOptions = {}): string {
  const id = options.id === undefined ? undefined : String(options.id);
  const shownId: string = showToast({
    id,
    source: "sonner",
    tone,
    title: message,
    description: options.description,
    action: options.action,
    duration: options.duration,
    onDismiss: options.onDismiss && (() => options.onDismiss?.({ id: shownId })),
    onAutoClose: options.onAutoClose && (() => options.onAutoClose?.({ id: shownId })),
  });
  return shownId;
}

export const toast = Object.assign((message: ReactNode, options?: ToastOptions) => show("default", message, options), {
  message: (message: ReactNode, options?: ToastOptions) => show("default", message, options),
  info: (message: ReactNode, options?: ToastOptions) => show("default", message, options),
  success: (message: ReactNode, options?: ToastOptions) => show("success", message, options),
  error: (message: ReactNode, options?: ToastOptions) => show("error", message, options),
  /** Closes that toast, or every toast shown with this API (no callbacks, as sonner). */
  dismiss: (id?: string | number) => {
    if (id === undefined) closeAllToasts("sonner");
    else closeToast(String(id));
  },
});
